// 사이드바 - 레이드별 보스 목록 및 공통 자원 메뉴
import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Space, Tooltip, Spin, Typography, Badge } from 'antd';
import {
  PlusOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  StarOutlined,
  DatabaseOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TrophyOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { BossInfo } from '../../types';
import { bossApi } from '../../services/api';
import { raidApi } from '../../services/supabaseApi';
import { useApp } from '../../contexts/AppContext';
import TableExportButton from '../common/TableExportButton';

const { Sider } = Layout;
const { Text } = Typography;

interface RaidWithBosses {
  raid_code: string;
  raid_name: string;
  min_entry_level: number;
  dungeon_x: number;
  dungeon_y: number;
  boss_room?: {
    room_name: string;
  };
  bosses: any[];
}

const Sidebar: React.FC = () => {
  const { state, selectBoss, openBossModal, viewCommonResources, dispatch } = useApp();
  const [raidsWithBosses, setRaidsWithBosses] = useState<RaidWithBosses[]>([]);
  const [loading, setLoading] = useState(false);

  // 레이드 및 보스 목록 로드
  const loadRaidData = async () => {
    console.log('loadRaidData 시작');
    try {
      setLoading(true);
      console.log('raidApi 호출 전');
      const raids = await raidApi.getAllWithBosses();
      console.log('레이드 데이터:', raids);
      setRaidsWithBosses(raids);
    } catch (error) {
      console.error('레이드 목록 로드 실패:', error);
      // 에러 발생시 기존 방식으로 폴백
      try {
        const bosses = await bossApi.getAll();
        console.log('폴백: 보스 목록만 로드', bosses);
      } catch (e) {
        console.error('보스 목록도 실패:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRaidData();
    window.addEventListener('raidDataUpdated', loadRaidData);
    return () => window.removeEventListener('raidDataUpdated', loadRaidData);
  }, []);

  // 보스 선택 핸들러
  const handleBossSelect = async (entityCode: string) => {
    try {
      // boss_info_renewed 테이블에서 실제 보스 정보 가져오기
      const bossInfo = await bossApi.getByCode(entityCode);
      if (bossInfo) {
        selectBoss(bossInfo);
        // 보스 선택 시 기본 탭으로 이동
        dispatch({ type: 'SET_ACTIVE_TAB', payload: 'basic' });
      } else {
        console.warn(`보스 정보를 찾을 수 없음: ${entityCode}`);
      }
    } catch (error) {
      console.error('보스 정보 로드 실패:', error);
    }
  };

  // 레이드 레벨별 색상
  const getLevelColor = (level: number) => {
    if (level <= 10) return '#52c41a'; // green
    if (level <= 20) return '#1890ff'; // blue
    if (level <= 30) return '#722ed1'; // purple
    if (level <= 40) return '#fa8c16'; // orange
    return '#f5222d'; // red
  };

  // 메뉴 아이템 생성
  const menuItems: MenuProps['items'] = [
    {
      key: 'raid-management',
      label: (
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          <EnvironmentOutlined style={{ marginRight: 8 }} />
          레이드 관리
        </span>
      ),
      onClick: () => {
        // 레이드 관리 탭으로 이동
        dispatch({ type: 'SET_ACTIVE_TAB', payload: 'raid-management' });
        dispatch({ type: 'SET_VIEWING_COMMON', payload: { viewing: false, resourceType: null } });
        selectBoss(null);
      },
    },
    {
      key: 'drop-item-management',
      label: (
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          ⚔️ 드롭템 관리
        </span>
      ),
      onClick: () => {
        dispatch({ type: 'SET_ACTIVE_TAB', payload: 'drop-item-management' });
        dispatch({ type: 'SET_VIEWING_COMMON', payload: { viewing: false, resourceType: null } });
        selectBoss(null);
      },
    },
    {
      type: 'divider',
    },
    {
      key: 'raids',
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span style={{ fontSize: '13px' }}>레이드 목록</span>
          <Tooltip title="새 보스 추가">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined style={{ fontSize: '12px' }} />}
              onClick={(e) => {
                e.stopPropagation();
                openBossModal();
              }}
            />
          </Tooltip>
        </div>
      ),
      icon: <TrophyOutlined />,
      children: raidsWithBosses.map(raid => ({
        key: `raid-${raid.raid_code}`,
        label: (
          <div style={{ lineHeight: '1.2' }}>
            <div style={{ fontSize: '13px' }}>{raid.raid_name}</div>
            <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>
              Lv.{raid.min_entry_level}
            </div>
          </div>
        ),
        children: raid.bosses.map((boss, index) => ({
          key: `boss-${boss.entity_code}`,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span style={{ fontSize: '12px' }}>
                {boss.spawn_order === 1 && index > 0 && '🔄 '}
                {boss.entity_name}
              </span>
              <Text type="secondary" style={{ fontSize: '10px' }}>
                {boss.spawn_order === 1 && index > 0 ? '동시' : boss.spawn_order ? `${boss.spawn_order}번째` : ''}
              </Text>
            </div>
          ),
          onClick: () => handleBossSelect(boss.entity_code),
        })),
      })),
    },
    {
      type: 'divider',
    },
    {
      key: 'common',
      label: '공통 자원',
      icon: <DatabaseOutlined />,
      children: [
        {
          key: 'common-skills',
          label: '공통 스킬',
          icon: <ThunderboltOutlined />,
          onClick: () => viewCommonResources('skills'),
        },
        {
          key: 'common-effects',
          label: '공통 효과',
          icon: <StarOutlined />,
          onClick: () => viewCommonResources('effects'),
        },
      ],
    },
    {
      type: 'divider',
    },
    {
      key: 'tools',
      label: '도구',
      icon: <FolderOpenOutlined />,
      children: [
        {
          key: 'reload',
          label: '데이터 새로고침',
          icon: <ReloadOutlined />,
          onClick: () => {
            loadRaidData();
          },
        },
      ],
    },
  ];

  // 현재 선택된 메뉴 키 계산
  const getSelectedKeys = () => {
    if (state.viewingCommon) {
      return [`common-${state.commonResourceType}`];
    } else if (state.selectedBoss) {
      return [`boss-${state.selectedBoss.boss_code}`];
    }
    return [];
  };

  // 현재 열려있어야 할 메뉴 키 계산
  const getOpenKeys = () => {
    const keys = ['raids', 'common'];
    
    if (state.selectedBoss) {
      // 선택된 보스가 속한 레이드 찾기
      const raid = raidsWithBosses.find(r => 
        r.bosses.some(b => b.entity_code === state.selectedBoss?.boss_code)
      );
      if (raid) {
        keys.push(`raid-${raid.raid_code}`);
      }
    }
    
    return keys;
  };

  return (
    <Sider
      width={260}
      theme="light"
      collapsible
      collapsed={state.sidebarCollapsed}
      onCollapse={(collapsed) => dispatch({ type: 'TOGGLE_SIDEBAR' })}
      trigger={
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          {state.sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      }
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
      }}
    >
      <div style={{ 
        padding: '16px', 
        background: '#001529', 
        color: 'white',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: state.sidebarCollapsed ? '14px' : '18px',
      }}>
        {state.sidebarCollapsed ? 'RBM' : '레이드 보스 관리자'}
      </div>

      <div style={{ padding: '12px 16px' }}>
        <TableExportButton collapsed={state.sidebarCollapsed} />
      </div>

      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Spin tip="레이드 목록 로드 중..." />
        </div>
      ) : (
        <Menu
          mode="inline"
          selectedKeys={getSelectedKeys()}
          defaultOpenKeys={getOpenKeys()}
          items={menuItems}
          style={{ minHeight: 'calc(100% - 160px)', borderRight: 0 }}
        />
      )}
    </Sider>
  );
};

export default Sidebar;
